import { Capacitor } from '@capacitor/core';
import { saveAs } from 'file-saver';
import { toBlob, toPng } from 'html-to-image';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { describeCaptureError } from 'components/receipt/captureError';
import { shareFileBase64 } from 'utils/saver';

/**
 * `skipFonts` porque webfont não sobrevive ao data URL da captura, e `onImageErrorHandler`
 * porque logo que não carrega não pode levar o comprovante junto.
 */
const CAPTURE_OPTIONS = {
  pixelRatio: 2,
  backgroundColor: 'white',
  quality: 1,
  cacheBust: true,
  skipFonts: true,
  onImageErrorHandler: () => undefined,
};

/**
 * Baixar e imprimir um comprovante desenhado fora da tela.
 *
 * No app (Capacitor) não existe download nem a impressão do navegador: as duas ações viram
 * compartilhar a imagem, como já é no comprovante de saque.
 *
 * O erro sai por callback, não por toast, para o hook não depender do Chakra.
 */
export const useReceiptCapture = (filename: string, onError: (message: string) => void) => {
  const ref = useRef<HTMLDivElement>(null);
  const printInBrowser = useReactToPrint({ content: () => ref.current });

  const download = async () => {
    if (!ref.current) {
      onError('Não há dados do comprovante para gerar');
      return;
    }

    try {
      // Um quadro para o navegador terminar layout e pintura antes da captura.
      await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

      if (Capacitor.isNativePlatform()) {
        await shareFileBase64(filename, await toPng(ref.current, CAPTURE_OPTIONS));
        return;
      }

      const blob = await toBlob(ref.current, CAPTURE_OPTIONS);

      if (!blob || blob.size === 0) {
        throw new Error('Comprovante gerado vazio');
      }

      saveAs(blob, filename);
    } catch (err) {
      onError(describeCaptureError(err));
    }
  };

  const print = () => (Capacitor.isNativePlatform() ? download() : printInBrowser());

  return { ref, download, print };
};
